import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Web3Provider } from './contexts/Web3Context';
import PasswordGate from './components/PasswordGate';
import HomeScreen from './components/HomeScreen';
import TechnologyPage from './components/TechnologyPage';
import HowItWorksPage from './components/HowItWorksPage';
import ContactPage from './components/ContactPage';
import LoginScreen from './components/LoginScreen';
import MainLayout from './components/Layout/MainLayout';
import Dashboard from './components/Dashboard';
import TokenDashboard from './components/TokenDashboard';
import HolderDashboard from './components/holder/HolderDashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import AssetDetail from './components/AssetDetail';
import Vesting from './components/Vesting';
import Distributions from './components/Distributions';
import Wallets from './components/Wallets';
import Settings from './components/Settings';
import Users from './components/admin/Users';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useAuth();
  
  if (!state.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useAuth();
  
  if (!state.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (state.user?.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { state } = useAuth();

  return (
    <Routes>
      <Route path="/" element={!state.isAuthenticated ? <HomeScreen /> : <Navigate to="/dashboard" replace />} />
      <Route path="/technology" element={<TechnologyPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/contact-us" element={<ContactPage />} />
      <Route path="/login" element={!state.isAuthenticated ? <LoginScreen /> : <Navigate to="/dashboard" replace />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/holder"
        element={
          <ProtectedRoute>
            <MainLayout>
              <HolderDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <MainLayout>
              <AdminDashboard />
            </MainLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/assets/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AssetDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vesting"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Vesting />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/distributions"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Distributions />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallets"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Wallets />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Settings />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <MainLayout>
              <Users />
            </MainLayout>
          </AdminRoute>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <PasswordGate>
      <Router>
        <ThemeProvider>
          <AuthProvider>
            <Web3Provider tokenAddress="">
              <AppRoutes />
            </Web3Provider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </PasswordGate>
  );
};

export default App;
